import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useProfile } from "@/hooks/useProfile";
import { useUserExpertise } from "@/hooks/useUserExpertise";
import { useQueryClient } from "@tanstack/react-query";
import { profileBioSchema, customTextSchema, organizationSchema, phoneSchema, passwordSchema } from "@/lib/validation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import Navbar from "@/components/Navbar";
import { Loader2, Plus, X, Download, Sparkles, Upload, Edit, RefreshCw } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ImageRegionSelector } from "@/components/ImageRegionSelector";
import { 
  useDomainsByIndustry, 
  useDomainCategories,
  useSimilarDomains,
  useSimilarCategories,
  insertDomain,
  insertCategory
} from "@/hooks/useDomainHierarchy";
import { SimilarItemSuggestions } from "@/components/shared/SimilarItemSuggestions";

const profileSchema = z.object({
  name: z.string().min(1, "Name is required").max(100),
  title: z.string().min(1, "Title is required").max(200),
  phone: z.string().optional().or(z.literal("")),
  linkedinBio: profileBioSchema,
  linkedinLink: z.string().url("Must be a valid URL").optional().or(z.literal("")),
  web3WalletAddress: z.string().max(100, "Wallet address must be less than 100 characters").optional().or(z.literal("")),
  industries: z.array(z.string()).optional().default([])
});

type ProfileFormValues = z.infer<typeof profileSchema>;

const industryOptions = [
  "Aerospace & Defense",
  "Agriculture & Agribusiness",
  "Arts & Culture",
  "Automotive & Mobility",
  "Beauty & Personal Care",
  "Charity & Faith-Based Organizations",
  "Chemicals",
  "Construction",
  "Consumer Electronics & Devices",
  "Consumer Packaged Goods (CPG)",
  "Education & Training",
  "Energy",
  "Environmental & Sustainability",
  "Events & Conferences",
  "Fashion & Apparel",
  "Financial Services",
  "Foodservice & Catering",
  "Gaming & Esports",
  "Government",
  "Healthcare & Life Sciences",
  "Home & Building Services",
  "Hospitality & Travel",
  "HR & Staffing",
  "Industrial Equipment & Services",
  "Insurance Services & Brokers",
  "Legal & Compliance Tech",
  "Logistics Real Estate",
  "Manufacturing",
  "Marketing & Advertising",
  "Media & Entertainment",
  "Media Production & Creative Services",
  "Mining & Metals",
  "Nonprofit & NGOs",
  "Ocean Economy & Marine",
  "Oilfield Services & Equipment",
  "Pharmaceutical Distribution",
  "Printing & Packaging",
  "Professional Services",
  "R&D & Scientific Services",
  "Real Estate",
  "Retail & eCommerce",
  "Security & Defense Services",
  "Space & New Space Economy",
  "Sports & Recreation",
  "Technology",
  "Telecommunications",
  "Transportation & Logistics",
  "Venture & Startup Ecosystem",
  "Waste Management & Recycling",
];

export default function EditProfile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { profile, isLoading: profileLoading } = useProfile();
  const { data: userExpertise, isLoading: expertiseLoading } = useUserExpertise(user?.id);
  const queryClient = useQueryClient();
  const hasLoadedExpertise = useRef(false);

  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [selectedExpertise, setSelectedExpertise] = useState<Array<{
    industry: string;
    domainId: string;
    domainName: string;
    categoryId: string;
    categoryName: string;
  }>>([]);
  const [organizations, setOrganizations] = useState<string[]>([]);
  const [newOrganization, setNewOrganization] = useState("");
  const [originalPrompt, setOriginalPrompt] = useState<string>("");
  const [refinementFeedback, setRefinementFeedback] = useState<string>("");
  const [isRefining, setIsRefining] = useState(false);
  const [referenceImage, setReferenceImage] = useState<string | null>(null);
  const [iterationCount, setIterationCount] = useState(0);
  const [selectedIndustry, setSelectedIndustry] = useState<string>("");
  const [selectedDomain, setSelectedDomain] = useState<{ id: string; name: string } | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<{ id: string; name: string }[]>([]);
  const [customDomain, setCustomDomain] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [imageSource, setImageSource] = useState<"generated" | "uploaded">("generated");
  const [isEditingRegion, setIsEditingRegion] = useState(false);
  const [isApplyingEdit, setIsApplyingEdit] = useState(false);
  const [editHistory, setEditHistory] = useState<string[]>([]);
  const [customIndustry, setCustomIndustry] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: "",
      title: "",
      phone: "",
      linkedinBio: "",
      linkedinLink: "",
      web3WalletAddress: "",
      industries: []
    }
  });

  // Populate form with existing data
  useEffect(() => {
    if (profile) {
      form.reset({
        name: profile.display_name || "",
        title: profile.title || "",
        phone: profile.phone || "",
        linkedinBio: profile.bio || "",
        linkedinLink: profile.linkedin_url || "",
        web3WalletAddress: profile.web3_wallet_address || "",
        industries: Array.isArray(profile.industries) ? profile.industries : []
      });
      
      // Load organizations
      if (Array.isArray(profile.organizations)) {
        setOrganizations(profile.organizations);
      } else if (profile.organization) {
        // Backward compatibility: if old single organization exists
        setOrganizations([profile.organization]);
      }
      
      if (profile.avatar_url) {
        setGeneratedImage(profile.avatar_url);
      }
    }
  }, [profile, form]);

  // Fetch domains filtered by selected industry
  const { data: domainsForIndustry = [], isLoading: domainsLoading } = useDomainsByIndustry(selectedIndustry);
  const { data: categoriesForDomain = [] } = useDomainCategories(selectedDomain?.id);
  
  // Fuzzy matching for similar items
  const { data: similarDomains = [] } = useSimilarDomains(customDomain);
  const { data: similarCategories = [] } = useSimilarCategories(customCategory, selectedDomain?.id);

  // Populate expertise only on initial load
  useEffect(() => {
    if (userExpertise && !hasLoadedExpertise.current) {
      const loadedExpertise = userExpertise.map(exp => ({
        industry: exp.industry_name || "",
        domainId: exp.domain_id || "",
        domainName: exp.domain_name,
        categoryId: exp.category_id || "",
        categoryName: exp.category
      }));
      setSelectedExpertise(loadedExpertise);
      hasLoadedExpertise.current = true;
    }
  }, [userExpertise]);

  // Track unsaved changes
  useEffect(() => {
    const formChanged = form.formState.isDirty;
    setHasUnsavedChanges(formChanged);
  }, [form.formState.isDirty]);

  // Warn user before closing/refreshing browser
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);


  const addExpertiseArea = async () => {
    if (!selectedIndustry || !selectedDomain || selectedCategories.length === 0) return;

    try {
      let finalDomainId = selectedDomain.id;
      let finalDomainName = selectedDomain.name;

      // Handle custom domain
      if (selectedDomain.id === "custom" && customDomain) {
        customTextSchema.parse(customDomain);
        const newDomain = await insertDomain(customDomain, "", undefined);
        finalDomainId = newDomain.id;
        finalDomainName = newDomain.name;
        await queryClient.invalidateQueries({ queryKey: ['domains'] });
        toast({
          title: "Domain added",
          description: `"${customDomain}" has been added!`
        });
      }

      // Loop through all selected categories
      for (const selectedCat of selectedCategories) {
        let finalCategoryId = selectedCat.id;
        let finalCategoryName = selectedCat.name;

        // Handle custom category
        if (selectedCat.id === "custom" && customCategory && finalDomainId) {
          customTextSchema.parse(customCategory);
          const newCategory = await insertCategory(finalDomainId, customCategory);
          finalCategoryId = newCategory.id;
          finalCategoryName = newCategory.name;
          await queryClient.invalidateQueries({ queryKey: ['domain_categories'] });
          toast({
            title: "Category added",
            description: `"${customCategory}" has been added!`
          });
        }

        // Check for duplicates
        const exists = selectedExpertise.some(e => 
          e.industry === selectedIndustry && 
          e.domainId === finalDomainId && 
          e.categoryId === finalCategoryId
        );

        if (!exists) {
          setSelectedExpertise(prev => [...prev, {
            industry: selectedIndustry,
            domainId: finalDomainId,
            domainName: finalDomainName,
            categoryId: finalCategoryId,
            categoryName: finalCategoryName
          }]);
        }
      }
      
      // Reset selections
      setSelectedIndustry("");
      setSelectedDomain(null);
      setSelectedCategories([]);
      setCustomDomain("");
      setCustomCategory("");
    } catch (error: any) {
      console.error('Error adding expertise:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add expertise",
        variant: "destructive"
      });
    }
  };

  const removeExpertiseArea = (expertiseToRemove: typeof selectedExpertise[0]) => {
    setSelectedExpertise(selectedExpertise.filter(exp => 
      !(exp.industry === expertiseToRemove.industry && 
        exp.domainId === expertiseToRemove.domainId && 
        exp.categoryId === expertiseToRemove.categoryId)
    ));
  };

  const addOrganization = () => {
    if (newOrganization.trim()) {
      try {
        organizationSchema.parse(newOrganization.trim());
        if (!organizations.includes(newOrganization.trim())) {
          setOrganizations([...organizations, newOrganization.trim()]);
          setNewOrganization("");
        }
      } catch (error) {
        if (error instanceof z.ZodError) {
          toast({
            title: "Validation Error",
            description: error.issues[0].message,
            variant: "destructive"
          });
        }
      }
    }
  };

  const removeOrganization = (index: number) => {
    setOrganizations(organizations.filter((_, i) => i !== index));
  };

  const handlePasswordChange = async () => {
    if (!newPassword || !confirmPassword) {
      toast({
        title: "Missing information",
        description: "Please fill in both password fields",
        variant: "destructive"
      });
      return;
    }

    try {
      passwordSchema.parse(newPassword);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Invalid password",
          description: error.issues[0].message,
          variant: "destructive"
        });
        return;
      }
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same",
        variant: "destructive"
      });
      return;
    }

    setIsChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast({
        title: "Password updated",
        description: "Your password has been changed successfully"
      });

      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      toast({
        title: "Password change failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const downloadGibNifty = () => {
    if (!generatedImage) return;

    try {
      const base64Data = generatedImage.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const userName = form.getValues().name || 'user';
      
      link.href = url;
      link.download = `gibnifty-${userName.replace(/\s+/g, '-').toLowerCase()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Download started",
        description: "Your GibNifty image is being downloaded"
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download failed",
        description: "Failed to download the image",
        variant: "destructive"
      });
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PNG, JPG, or WEBP image",
        variant: "destructive"
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 5MB",
        variant: "destructive"
      });
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        if (generatedImage) {
          setEditHistory([...editHistory, generatedImage]);
        }
        setGeneratedImage(base64);
        setImageSource("uploaded");
        setIterationCount(0);
        toast({
          title: "Image uploaded",
          description: "Your custom GibNifty has been uploaded successfully"
        });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: "Failed to upload the image",
        variant: "destructive"
      });
    }
  };

  const handleRegionEdit = async (regionDescription: string, editInstructions: string) => {
    if (!user || !generatedImage) return;

    setIsApplyingEdit(true);
    try {
      const { data, error } = await supabase.functions.invoke('edit-gibnifty-region', {
        body: {
          imageUrl: generatedImage,
          regionDescription,
          editInstructions
        }
      });

      if (error) throw error;

      if (data?.image) {
        setEditHistory([...editHistory, generatedImage]);
        setGeneratedImage(data.image);
        setIterationCount(prev => prev + 1);
        setIsEditingRegion(false);
        toast({
          title: "Region edited successfully!",
          description: "Your GibNifty has been updated"
        });
      }
    } catch (error: any) {
      console.error("Error editing region:", error);
      toast({
        title: "Edit failed",
        description: error.message || "Failed to edit the selected region",
        variant: "destructive"
      });
    } finally {
      setIsApplyingEdit(false);
    }
  };

  const handleReferenceImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Image must be less than 5MB",
        variant: "destructive"
      });
      return;
    }

    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setReferenceImage(reader.result as string);
        toast({
          title: "Reference image uploaded",
          description: "This image will be included in your GibNifty"
        });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading reference image:', error);
      toast({
        title: "Upload failed",
        description: "Failed to upload reference image",
        variant: "destructive"
      });
    }
  };

  const refineGibNifty = async () => {
    if (!user || !refinementFeedback.trim()) {
      toast({
        title: "Missing information",
        description: "Please provide feedback for refinement",
        variant: "destructive"
      });
      return;
    }

    setIsRefining(true);
    try {
      // Use original prompt if available, otherwise create a generic one
      const promptToUse = originalPrompt || `Create a professional GibNifty avatar based on the user's profile. Style: Semi-realistic, digital-painterly, Pixar-like 3D illustration of an anthropomorphic owl.`;
      const { data, error } = await supabase.functions.invoke('refine-gibnifty', {
        body: {
          originalPrompt: promptToUse,
          feedback: refinementFeedback.trim(),
          referenceImageUrl: referenceImage
        }
      });

      if (error) throw error;

      if (data?.image) {
        setEditHistory([...editHistory, generatedImage!]);
        setGeneratedImage(data.image);
        setIterationCount(prev => prev + 1);
        setRefinementFeedback("");
        setReferenceImage(null);
        toast({
          title: "GibNifty refined!",
          description: "Your profile picture has been updated with your feedback"
        });
      }
    } catch (error: any) {
      console.error("Error refining GibNifty:", error);
      toast({
        title: "Refinement failed",
        description: error.message || "Failed to refine GibNifty",
        variant: "destructive"
      });
    } finally {
      setIsRefining(false);
    }
  };

  const generateGibNifty = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to generate your GibNifty",
        variant: "destructive"
      });
      return;
    }

    const values = form.getValues();
    if (!values.name || !values.title) {
      toast({
        title: "Missing information",
        description: "Please fill in your name and title before generating",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);
    try {
      const domainsList = selectedExpertise.map(e => 
        `${e.industry}: ${e.domainName} - ${e.categoryName}`
      ).join(", ");

      let industriesList = values.industries.filter(i => i !== "Other").join(", ");
      if (values.industries.includes("Other") && customIndustry.trim()) {
        industriesList += (industriesList ? ", " : "") + customIndustry.trim();
      }

      const safeBio = values.linkedinBio.substring(0, 500);
      const safeDomains = domainsList.substring(0, 200);
      const prompt = `Create a GibNifty for ${values.name}. They work as ${values.title}. ${safeBio}. Their domains of expertise include: ${safeDomains || "general construction knowledge"}. They work in the following industries: ${industriesList}. 

Style: Semi-realistic, digital-painterly, Pixar-like 3D illustration of an anthropomorphic owl. The owl should NOT be snowy white (that's reserved for Giblet). Use appropriate color palette based on their profession and personality. Include a name tag with "${values.name} – ${values.title}". Add contextual props and background elements that reflect their expertise in ${industriesList}. The owl should look professional yet approachable. Include subtle iSPAI branding. High quality, distinctive, cohesive with the GibNifty visual identity.`.substring(0, 2000);

      setOriginalPrompt(prompt);

      const { data, error } = await supabase.functions.invoke('generate-gibnifty', {
        body: { prompt }
      });

      if (error) throw error;

      if (data?.image) {
        setGeneratedImage(data.image);
        setIterationCount(1);
        toast({
          title: "GibNifty generated!",
          description: "Your profile picture has been created successfully"
        });
      }
    } catch (error: any) {
      console.error("Error generating GibNifty:", error);
      toast({
        title: "Generation failed",
        description: error.message || "Failed to generate GibNifty",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const onSubmit = async (values: ProfileFormValues) => {
    console.log('Form submission started with values:', values);
    
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to update your profile",
        variant: "destructive"
      });
      return;
    }

    try {
      let industriesArray = values.industries.filter(i => i !== "Other");
      if (values.industries.includes("Other") && customIndustry.trim()) {
        industriesArray.push(customIndustry.trim());
      }
      
      console.log('Updating profile with industries:', industriesArray);
      console.log('Updating profile with organizations:', organizations);

      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          display_name: values.name,
          bio: values.linkedinBio,
          avatar_url: generatedImage,
          title: values.title,
          phone: values.phone,
          linkedin_url: values.linkedinLink,
          web3_wallet_address: values.web3WalletAddress || null,
          industries: industriesArray,
          organizations: organizations,
        })
        .eq('id', user.id);

      if (profileError) throw profileError;

      // Update expertise - delete old and insert new
      console.log('Deleting existing expertise...');
      const { error: deleteError } = await supabase
        .from('user_expertise')
        .delete()
        .eq('user_id', user.id);

      if (deleteError) {
        console.error('Delete error:', deleteError);
        throw deleteError;
      }

      console.log('Inserting new expertise:', selectedExpertise);
      if (selectedExpertise.length > 0) {
        console.log('Bulk inserting expertise records:', selectedExpertise.length);
        
        const expertiseRecords = selectedExpertise.map(exp => ({
          user_id: user.id,
          industry_name: exp.industry,
          domain_id: exp.domainId,
          domain_name: exp.domainName,
          category_id: exp.categoryId,
          category: exp.categoryName,
          subcategory: '' // Empty for new structure
        }));
        
        const { error: expertiseError } = await supabase
          .from('user_expertise')
          .insert(expertiseRecords);
        
        if (expertiseError) {
          console.error('Bulk expertise insert error:', expertiseError);
          throw expertiseError;
        }
      } else {
        console.log('No expertise to insert');
      }

      // Invalidate queries to ensure fresh data
      await queryClient.invalidateQueries({ 
        queryKey: ['user_expertise', user.id] 
      });
      await queryClient.invalidateQueries({ 
        queryKey: ['profile', user.id] 
      });

      toast({
        title: "Profile updated!",
        description: "Your mySPAI profile has been updated successfully"
      });

      // Navigate back to profile
      if (profile?.username) {
        navigate(`/myspai-profile/${profile.username}`);
      }
    } catch (error: any) {
      console.error("Error updating profile:", error);
      toast({
        title: "Update failed",
        description: error.message || "Failed to update profile",
        variant: "destructive"
      });
    }
  };

  if (profileLoading || expertiseLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Edit Your mySPAI Profile</h1>
          <p className="text-lg text-gray-600">
            Update your profile information, expertise domains, and GibNifty avatar
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-6">Basic Information</h2>
              
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your full name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title/Role *</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Senior Construction Manager" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />


                <div className="space-y-3">
                  <FormLabel>Organizations *</FormLabel>
                  <div className="space-y-2">
                    {organizations.map((org, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-muted rounded-md">
                        <span className="flex-1 text-sm">{org}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOrganization(index)}
                          className="h-6 w-6 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add organization"
                        value={newOrganization}
                        onChange={(e) => setNewOrganization(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addOrganization();
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={addOrganization}
                        disabled={!newOrganization.trim()}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {organizations.length === 0 && (
                      <p className="text-sm text-muted-foreground">Add at least one organization</p>
                    )}
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your phone number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="linkedinBio"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>LinkedIn Bio *</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Paste your LinkedIn bio or write a brief professional summary..."
                          className="min-h-[120px]"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        This helps us create a personalized GibNifty for you
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="linkedinLink"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>LinkedIn Profile URL</FormLabel>
                      <FormControl>
                        <Input placeholder="https://linkedin.com/in/yourprofile" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="web3WalletAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Web3 Wallet Address (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., yourname.eth or 0x..." {...field} />
                      </FormControl>
                      <FormDescription>Enter your ENS name or Ethereum wallet address</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-6">Expertise Areas</h2>
              
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-3">
                    <label className="block text-sm font-medium mb-2">Industry</label>
                    <Select
                      value={selectedIndustry}
                      onValueChange={(value) => {
                        setSelectedIndustry(value);
                        setSelectedDomain(null);
                        setSelectedCategories([]);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {industryOptions.map((industry) => (
                          <SelectItem key={industry} value={industry}>
                            {industry}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedIndustry && (
                    <div className="space-y-3">
                      <label className="block text-sm font-medium mb-2">Domain</label>
                      <Select
                        value={selectedDomain?.id}
                        onValueChange={(domainId) => {
                          const domain = domainsForIndustry.find(d => d.id === domainId);
                          if (domainId === "custom") {
                            setSelectedDomain({ id: "custom", name: "" });
                          } else if (domain) {
                            setSelectedDomain({ id: domain.id, name: domain.name });
                          }
                          setSelectedCategories([]);
                          if (domainId !== "custom") setCustomDomain("");
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Domain" />
                        </SelectTrigger>
                        <SelectContent>
                          {domainsLoading ? (
                            <SelectItem value="loading" disabled>Loading...</SelectItem>
                          ) : (
                            <>
                              {domainsForIndustry.map((domain) => (
                                <SelectItem key={domain.id} value={domain.id}>
                                  {domain.name}
                                </SelectItem>
                              ))}
                              <SelectItem value="custom">+ Add Custom Domain</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                      {selectedDomain?.id === "custom" && (
                        <>
                          <Input
                            placeholder="Enter custom domain"
                            value={customDomain}
                            onChange={(e) => setCustomDomain(e.target.value)}
                          />
                          {customDomain && similarDomains.length > 0 && (
                            <SimilarItemSuggestions
                              items={similarDomains}
                              onSelect={(item) => {
                                const domain = domainsForIndustry.find(d => d.id === item.id);
                                if (domain) {
                                  setSelectedDomain({ id: domain.id, name: domain.name });
                                  setCustomDomain("");
                                }
                              }}
                              label="domain"
                            />
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {selectedDomain && selectedDomain.id !== "custom" && (
                    <div className="space-y-3">
                      <label className="block text-sm font-medium mb-2">Subdomains (select multiple)</label>
                      <div className="max-h-48 overflow-y-auto border rounded-md p-3 space-y-2">
                        {categoriesForDomain.map((category) => (
                          <label key={category.id} className="flex items-center gap-2 cursor-pointer hover:bg-muted p-2 rounded">
                            <input
                              type="checkbox"
                              checked={selectedCategories.some(cat => cat.id === category.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCategories([...selectedCategories, { id: category.id, name: category.name }]);
                                } else {
                                  setSelectedCategories(selectedCategories.filter(cat => cat.id !== category.id));
                                }
                              }}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{category.name}</span>
                          </label>
                        ))}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (!selectedCategories.some(cat => cat.id === "custom")) {
                            setSelectedCategories([...selectedCategories, { id: "custom", name: "" }]);
                          }
                        }}
                        className="w-full"
                      >
                        + Add Custom Subdomain
                      </Button>
                      {selectedCategories.some(cat => cat.id === "custom") && (
                        <Input
                          placeholder="Enter custom category"
                          value={customCategory}
                          onChange={(e) => setCustomCategory(e.target.value)}
                        />
                      )}
                    </div>
                  )}
                </div>

                <Button
                  type="button"
                  onClick={addExpertiseArea}
                  disabled={
                    !selectedIndustry ||
                    !selectedDomain ||
                    selectedCategories.length === 0 ||
                    (selectedDomain.id === "custom" && !customDomain) ||
                    (selectedCategories.some(cat => cat.id === "custom") && !customCategory)
                  }
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Expertise Area
                </Button>
              </div>

              {selectedExpertise.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold mb-3">Selected Expertise:</h3>
                  {Object.entries(
                    selectedExpertise.reduce((acc, area) => {
                      const key = `${area.industry}|${area.domainId}|${area.domainName}`;
                      if (!acc[key]) {
                        acc[key] = {
                          industry: area.industry,
                          domainId: area.domainId,
                          domainName: area.domainName,
                          categories: []
                        };
                      }
                      acc[key].categories.push({
                        categoryId: area.categoryId,
                        categoryName: area.categoryName
                      });
                      return acc;
                    }, {} as Record<string, { industry: string; domainId: string; domainName: string; categories: Array<{ categoryId: string; categoryName: string }> }>)
                  ).map(([key, group]) => (
                    <div
                      key={key}
                      className="p-3 bg-muted rounded-lg space-y-2"
                    >
                      <p className="text-sm font-medium">
                        {group.industry} → {group.domainName}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {group.categories.map((category, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-1 px-2 py-1 bg-background rounded-md text-sm"
                          >
                            <span>{category.categoryName}</span>
                            <button
                              type="button"
                              onClick={() => removeExpertiseArea({
                                industry: group.industry,
                                domainId: group.domainId,
                                domainName: group.domainName,
                                categoryId: category.categoryId,
                                categoryName: category.categoryName
                              })}
                              className="hover:text-destructive"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-6">Update Your GibNifty</h2>
              
              <div className="text-center space-y-4">
                {generatedImage ? (
                  <div className="space-y-6">
                    {!isEditingRegion ? (
                      <>
                        <div className="space-y-2">
                          <div className="relative inline-block">
                            <img
                              src={generatedImage}
                              alt="Your GibNifty"
                              className="w-64 h-64 mx-auto rounded-lg object-cover"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="absolute top-2 right-2 bg-background/80 backdrop-blur-sm"
                              onClick={() => {
                                setGeneratedImage(null);
                                setIterationCount(0);
                                setOriginalPrompt("");
                                setEditHistory([]);
                              }}
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          </div>
                          {iterationCount > 0 && (
                            <p className="text-sm text-gray-500">
                              {imageSource === "uploaded"
                                ? "Uploaded"
                                : `Version ${iterationCount}`}
                              {editHistory.length > 0 &&
                                ` (${editHistory.length + 1} total edits)`}
                            </p>
                          )}
                        </div>

                        <div className="flex gap-3 justify-center flex-wrap">
                          <Button
                            type="button"
                            onClick={downloadGibNifty}
                            variant="outline"
                          >
                            <Download className="mr-2 h-4 w-4" />
                            Download
                          </Button>
                          <Button
                            type="button"
                            onClick={() => setIsEditingRegion(true)}
                            variant="outline"
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Edit Region
                          </Button>
                          <Button
                            type="button"
                            onClick={generateGibNifty}
                            disabled={isGenerating}
                            variant="outline"
                          >
                            {isGenerating ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Regenerating...
                              </>
                            ) : (
                              "Regenerate"
                            )}
                          </Button>
                        </div>

                        <Collapsible>
                          <CollapsibleTrigger asChild>
                            <Button type="button" variant="ghost" className="w-full">
                              <Sparkles className="mr-2 h-4 w-4" />
                              Refine with Feedback
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="mt-4 space-y-4">
                            <div className="text-left">
                              <label className="block text-sm font-medium mb-2">
                                How would you like to adjust your entire GibNifty?
                              </label>
                              <Textarea
                                placeholder="e.g., Make the owl wear glasses, add a hard hat, use warmer colors..."
                                value={refinementFeedback}
                                onChange={(e) => setRefinementFeedback(e.target.value)}
                                className="min-h-[100px]"
                                maxLength={500}
                              />
                              <p className="text-sm text-gray-500 mt-1">
                                {refinementFeedback.length}/500 characters
                              </p>
                            </div>

                            <div className="text-left">
                              <label className="block text-sm font-medium mb-2">
                                Upload Reference Image (Optional)
                              </label>
                              <p className="text-sm text-muted-foreground mb-2">
                                Upload a logo or image to include in your GibNifty
                              </p>
                              <input
                                type="file"
                                id="referenceImage"
                                accept="image/*"
                                onChange={handleReferenceImageUpload}
                                className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                              />
                              {referenceImage && (
                                <div className="mt-3 relative inline-block">
                                  <img 
                                    src={referenceImage} 
                                    alt="Reference" 
                                    className="h-20 w-20 object-cover rounded border"
                                  />
                                  <button
                                    onClick={() => setReferenceImage(null)}
                                    className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                            <Button
                              type="button"
                              onClick={refineGibNifty}
                              disabled={isRefining || !refinementFeedback.trim()}
                              className="w-full"
                            >
                              {isRefining ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Refining...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="mr-2 h-4 w-4" />
                                  Refine Entire Image
                                </>
                              )}
                            </Button>
                          </CollapsibleContent>
                        </Collapsible>
                      </>
                    ) : (
                      <ImageRegionSelector
                        imageUrl={generatedImage}
                        onEdit={handleRegionEdit}
                        onCancel={() => setIsEditingRegion(false)}
                        isEditing={isApplyingEdit}
                      />
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-gray-600">
                      Generate your unique GibNifty avatar or upload your own
                    </p>
                    
                    <div className="flex gap-3 justify-center flex-wrap">
                      <Button
                        type="button"
                        onClick={generateGibNifty}
                        disabled={isGenerating}
                        size="lg"
                      >
                        {isGenerating ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          "Generate My GibNifty"
                        )}
                      </Button>

                      <div className="relative">
                        <input
                          type="file"
                          id="gibnifty-upload"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="lg"
                          onClick={() =>
                            document.getElementById("gibnifty-upload")?.click()
                          }
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          Upload Your Own
                        </Button>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground">
                      Supported formats: PNG, JPG, WEBP (max 5MB)
                    </p>
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-6">Change Password</h2>
              <p className="text-muted-foreground mb-6">
                Update your account password. Make sure it's at least 6 characters long.
              </p>
              
              <div className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                  />
                </div>

                <Button
                  type="button"
                  onClick={handlePasswordChange}
                  disabled={isChangingPassword || !newPassword || !confirmPassword}
                  variant="outline"
                >
                  {isChangingPassword ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Changing Password...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </Button>
              </div>
            </Card>

            {Object.keys(form.formState.errors).length > 0 && (
              <Card className="p-4 bg-destructive/10 border-destructive">
                <div className="flex items-start gap-3">
                  <div className="text-destructive">⚠️</div>
                  <div>
                    <h3 className="font-semibold text-destructive mb-2">Please fix the following errors:</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-destructive">
                      {Object.entries(form.formState.errors).map(([key, error]) => (
                        <li key={key}>
                          {key === 'industries' && 'Select at least one industry'}
                          {key === 'name' && error?.message?.toString()}
                          {key === 'title' && error?.message?.toString()}
                          {key === 'organization' && error?.message?.toString()}
                          {key === 'phone' && error?.message?.toString()}
                          {key === 'linkedinBio' && error?.message?.toString()}
                          {key === 'linkedinLink' && error?.message?.toString()}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            )}

            <div className="flex gap-4 justify-center">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => {
                  if (hasUnsavedChanges) {
                    setShowUnsavedDialog(true);
                  } else {
                    navigate(-1);
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                size="lg" 
                className="px-12"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </form>
        </Form>

        <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes. Are you sure you want to leave? All changes will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Stay on Page</AlertDialogCancel>
              <AlertDialogAction onClick={() => navigate(-1)}>
                Leave Without Saving
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
